import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import FileSaver from 'file-saver';

import ApplicationLayout from 'v3/layouts/Application';
import { DataTable, Text, Button } from 'v3/components';
import Loader from 'v3/components/Loader';
import { useSnackbar } from 'v3/components/Snackbar';
import { usePagination } from 'hooks';
import {
  exportShippingCompanyRequests,
  listShippingCompanyRequests,
} from 'v3/services/shippingCompanyRequest';

import { formatDateTime, normalizeCounts } from '../shared/status';
import StatusBadge from '../shared/StatusBadge';
import Filter from './Filter';
import StatusCounters from './StatusCounters';

const DEFAULT_PER_PAGE = 15;
const PER_PAGE_OPTIONS = [{ v: 10 }, { v: 15 }, { v: 25 }, { v: 50 }];

const emptyFilters = {
  shippingDocument: '',
  email: '',
  userDocument: '',
  statusSlug: '',
};

export default function ShippingCompanyRequestList() {
  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [data, setData] = useState([]);
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [loadingExport, setLoadingExport] = useState(false);

  const snackbar = useSnackbar();
  const pagination = usePagination({ initialPerPage: DEFAULT_PER_PAGE });

  useEffect(() => {
    let ignore = false;

    async function fetchRequests() {
      setLoading(true);
      setError(null);
      try {
        const {
          rows,
          total,
          lastPage,
          counts: statusCounts,
        } = await listShippingCompanyRequests({
          ...appliedFilters,
          page: pagination.currentPage,
          perPage: pagination.perPage,
        });
        if (ignore) return;
        setData(rows);
        setCounts(statusCounts);
        pagination.setTotal(total);
        pagination.setLastPage(lastPage);
      } catch (err) {
        if (ignore) return;
        setData([]);
        setCounts([]);
        pagination.setTotal(0);
        pagination.setLastPage(1);
        setError(err.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    fetchRequests();

    return () => {
      ignore = true;
    };
  }, [appliedFilters, pagination.perPage, pagination.currentPage, reloadKey]);

  function handleFilter() {
    setAppliedFilters({ ...filters });
    pagination.setCurrentPage(1);
  }

  function handleClear() {
    const cleared = { ...filters, shippingDocument: '', email: '', statusSlug: '' };
    setFilters(cleared);
    setAppliedFilters(cleared);
    pagination.setCurrentPage(1);
  }

  // Exporta os filtros aplicados (os que produziram a grid), não o que está digitado no formulário.
  async function handleExport() {
    setLoadingExport(true);
    try {
      const { blob, filename } = await exportShippingCompanyRequests(appliedFilters);
      FileSaver.saveAs(blob, filename);
    } catch (err) {
      snackbar.show(<Text>{err.message}</Text>, { type: 'error' });
    } finally {
      setLoadingExport(false);
    }
  }

  function handlePerPageChange(perPage) {
    pagination.setPerPage(perPage);
    pagination.setCurrentPage(1);
  }

  return (
    <ApplicationLayout
      contentFluid
      title="Solicitações de Transportadora"
      RightComponent={
        <Button
          variant="secondary"
          loading={loadingExport}
          disabled={loading}
          onClick={handleExport}
        >
          <Text color="dark" weight={500} type="regular">
            Exportar
          </Text>
        </Button>
      }
    >
      <Filter
        filters={filters}
        setFilters={setFilters}
        onFilter={handleFilter}
        onClear={handleClear}
        loading={loading}
      />

      <StatusCounters counts={normalizeCounts(counts)} loading={loading} error={!!error} />

      {error && (
        <div className="d-flex align-items-center mb-3">
          <Text type="regular" color="danger" className="mr-3">
            {error}
          </Text>
          <Button variant="secondary" size="sm" onClick={() => setReloadKey((key) => key + 1)}>
            <Text color="dark" weight={500}>
              Tentar novamente
            </Text>
          </Button>
        </div>
      )}

      <div className="d-flex align-items-center mb-2" style={{ minHeight: 20 }}>
        <Loader loading={loading} />
        {loading && (
          <Text type="label" color="gray" className="ml-2">
            Carregando solicitações...
          </Text>
        )}
      </div>

      <DataTable
        data={data}
        loading={loading}
        HeaderRightComponent={
          <Text type="regular" color="dark" weight={500} data-testid="requests-total">
            Total de registros: {pagination.total}
          </Text>
        }
        withFilterOption={false}
        withPagination
        total={pagination.total}
        pages={pagination.lastPage}
        currentPage={pagination.currentPage}
        defaultPerPage={DEFAULT_PER_PAGE}
        perPageOptions={PER_PAGE_OPTIONS}
        perPagePosition="both"
        onPageChanged={pagination.setCurrentPage}
        onPerPageChanged={handlePerPageChange}
        emptyMessage="Nenhuma solicitação encontrada"
        tableStyles={{ whiteSpace: 'nowrap' }}
        columns={[
          {
            name: 'ID',
            selector: (row) => row.id,
          },
          {
            name: 'Status',
            render: (row) => <StatusBadge status={row.status} />,
          },
          {
            name: 'Razão Social',
            selector: (row) => row.shippingName,
          },
          {
            name: 'CNPJ / CPF',
            selector: (row) => row.shippingDocument,
          },
          {
            name: 'CPF Usuário',
            selector: (row) => row.userDocument,
          },
          {
            name: 'Nome Usuário',
            selector: (row) => row.userName,
          },
          {
            name: 'Telefone Usuário',
            selector: (row) => row.userPhone,
          },
          {
            name: 'Data Solicitação',
            selector: (row) => formatDateTime(row.createdAt),
          },
          {
            name: 'Ação',
            render: (row) => (
              <Link to={`/transportadoras/solicitacoes/${row.id}`}>
                <Button size="sm" variant="primary">
                  <Text weight={500} color="white" type="regular">
                    Visualizar
                  </Text>
                </Button>
              </Link>
            ),
          },
        ]}
      />
    </ApplicationLayout>
  );
}
